import { useState, useCallback, useRef, useEffect } from "react";
import api from "../../../../../../../services/api";

export const ANEXO_ENTITY_TYPE_NODE = "DiagramaEquipamentoNode";
export const ANEXO_TYPE_NODE = "equipamento";

// Estado inicial (vazio) de anexos de um nó ainda não inspecionado
export const emptyNodeAnexosEntry = {
  saved: [],
  newFiles: [],
  deletedIds: [],
  loaded: false,
  loading: false,
};

// Hook para gerenciar ciclo de vida, cache, upload e exclusão em cascata de anexos dos nós
export default function useNodeAnexos({
  circuitoId = "",
  circuitoPersisted = false,
  editMode = true,
  selectedNodeId = null,
  nodesRef,
  setSnackbar,
}) {
  // Anexos de cada nó em memória agrupados por nodeId
  const [nodeAnexosById, setNodeAnexosById] = useState({});

  // Ref para controle de status de fetch sob demanda ("loading" | "done") por nodeId
  const nodeAnexosFetchStatusRef = useRef({});

  // Fila de IDs de anexos salvos de nós excluídos do diagrama para deletar no próximo save
  const [cascadeDeletedAnexoIds, setCascadeDeletedAnexoIds] = useState([]);

  // Monta o entityId composto (circuitoId:nodeId) para vinculação precisa do anexo ao circuito
  const buildEntityId = useCallback(
    (nodeId) => (circuitoId ? `${circuitoId}:${nodeId}` : nodeId),
    [circuitoId],
  );

  // Busca anexos salvos de um nó específico sob demanda
  const carregarAnexosDoNode = useCallback(
    (nodeId) => {
      if (!nodeId || !circuitoPersisted) return;
      const statusAtual = nodeAnexosFetchStatusRef.current[nodeId];
      if (statusAtual === "done" || statusAtual === "loading") return;

      nodeAnexosFetchStatusRef.current[nodeId] = "loading";
      const entityId = buildEntityId(nodeId);

      api
        .post("/anexos/findMany", {
          entityType: ANEXO_ENTITY_TYPE_NODE,
          entityId,
        })
        .then((res) => {
          nodeAnexosFetchStatusRef.current[nodeId] = "done";
          setNodeAnexosById((prev) => ({
            ...prev,
            [nodeId]: {
              ...(prev?.[nodeId] || emptyNodeAnexosEntry),
              saved: res?.data || [],
              loaded: true,
              loading: false,
            },
          }));
        })
        .catch((error) => {
          console.error("Erro ao buscar anexos do equipamento:", error);
          nodeAnexosFetchStatusRef.current[nodeId] = "done";
          setNodeAnexosById((prev) => ({
            ...prev,
            [nodeId]: {
              ...(prev?.[nodeId] || emptyNodeAnexosEntry),
              loaded: true,
              loading: false,
            },
          }));
        });
    },
    [circuitoPersisted, buildEntityId],
  );

  // Efeito para carregar anexos sob demanda ao inspecionar um nó
  useEffect(() => {
    if (selectedNodeId && circuitoPersisted) {
      carregarAnexosDoNode(selectedNodeId);
    }
  }, [selectedNodeId, circuitoPersisted, carregarAnexosDoNode]);

  // Adiciona arquivos em memória como anexos pendentes de um nó
  const addNodeAnexoFiles = useCallback(
    (nodeId, arquivos) => {
      if (!editMode || !nodeId || !arquivos?.length) return;
      const novosArquivos = (arquivos || []).map((arquivo) => ({
        originalFile: arquivo,
        name: arquivo?.name,
        type: arquivo?.type,
        size: arquivo?.size,
        preview: URL.createObjectURL(arquivo),
        isPdf:
          arquivo?.type === "application/pdf" ||
          arquivo?.name?.toLowerCase?.()?.endsWith(".pdf"),
        isNew: true,
      }));
      setNodeAnexosById((prev) => ({
        ...prev,
        [nodeId]: {
          ...emptyNodeAnexosEntry,
          ...prev?.[nodeId],
          newFiles: [...(prev?.[nodeId]?.newFiles || []), ...novosArquivos],
        },
      }));
    },
    [editMode],
  );

  // Remove um anexo pendente ou enfileira um anexo salvo para exclusão
  const removeNodeAnexoFile = useCallback(
    (nodeId, arquivo) => {
      if (!editMode || !nodeId) return;
      setNodeAnexosById((prev) => {
        const entryAtual = prev?.[nodeId] || emptyNodeAnexosEntry;
        if (arquivo?.isNew) {
          if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
          return {
            ...prev,
            [nodeId]: {
              ...entryAtual,
              newFiles: (entryAtual?.newFiles || []).filter(
                (f) => f !== arquivo,
              ),
            },
          };
        }
        return {
          ...prev,
          [nodeId]: {
            ...entryAtual,
            saved: (entryAtual?.saved || []).filter(
              (f) => f?.id !== arquivo?.id,
            ),
            deletedIds: arquivo?.id
              ? [...(entryAtual?.deletedIds || []), arquivo.id]
              : entryAtual?.deletedIds || [],
          },
        };
      });
    },
    [editMode],
  );

  // Abre preview de arquivo novo ou dispara download de arquivo salvo
  const downloadNodeAnexoFile = useCallback(
    async (arquivo) => {
      try {
        if (arquivo?.isNew) {
          const isImage = arquivo?.type
            ? arquivo.type.startsWith("image/")
            : !arquivo?.isPdf;
          if (isImage) {
            const novaJanela = window?.open?.();
            if (novaJanela) {
              novaJanela?.document?.write?.(
                `<html><head><title>${arquivo?.name || ""}</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;background-color:#0e0e0e;min-height:100vh;"><img src="${arquivo?.preview}" alt="${arquivo?.name || ""}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`,
              );
              novaJanela?.document?.close?.();
            }
          } else {
            const link = document.createElement("a");
            link.href = arquivo?.preview;
            link.download = arquivo?.name || "documento";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          return;
        }

        setSnackbar?.({ children: "Baixando anexo...", severity: "info" });
        const response = await api.post(
          "/anexos/download",
          { id: arquivo?.id },
          { responseType: "blob" },
        );
        const blobUrl = URL.createObjectURL(response?.data);
        const mimeType = arquivo?.mimeType || "";
        const fileName = arquivo?.nome || arquivo?.name || "anexo";
        const isImage =
          mimeType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        const isPdf =
          mimeType === "application/pdf" ||
          fileName.toLowerCase().endsWith(".pdf");

        if (isImage || isPdf) {
          window?.open?.(blobUrl, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        console.error("Erro ao abrir ou baixar anexo do equipamento:", error);
        setSnackbar?.({
          children: "Erro ao carregar arquivo.",
          severity: "error",
        });
      }
    },
    [setSnackbar],
  );

  // Prepara exclusão em cascata de anexos vinculados a um nó removido
  const prepararRemocaoDeAnexosDoNode = useCallback(
    (nodeId) => {
      const entryAtual = nodeAnexosById?.[nodeId];

      (entryAtual?.newFiles || []).forEach((arquivo) => {
        if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
      });

      if (entryAtual?.loaded) {
        const idsSalvos = (entryAtual?.saved || [])
          .map((f) => f?.id)
          .filter(Boolean);
        if (idsSalvos?.length) {
          setCascadeDeletedAnexoIds((prev) => [...prev, ...idsSalvos]);
        }
      } else {
        api
          .post("/anexos/findMany", {
            entityType: ANEXO_ENTITY_TYPE_NODE,
            entityId: buildEntityId(nodeId),
          })
          .then((res) => {
            if (nodesRef?.current?.some((node) => node?.id === nodeId)) return;
            const ids = (res?.data || []).map((f) => f?.id).filter(Boolean);
            if (ids?.length)
              setCascadeDeletedAnexoIds((prev) => [...prev, ...ids]);
          })
          .catch((error) =>
            console.error(
              "Erro ao buscar anexos do equipamento removido:",
              error,
            ),
          );
      }
    },
    [nodeAnexosById, buildEntityId, nodesRef],
  );

  // Enfileira exclusão em cascata de anexos para múltiplos nós
  const enfileirarCascadeDelete = useCallback(
    (nodeIds = []) => {
      (nodeIds || []).forEach((nodeId) => {
        prepararRemocaoDeAnexosDoNode(nodeId);
      });
    },
    [prepararRemocaoDeAnexosDoNode],
  );

  // Limpa anexos de todos os nós removidos ao aplicar um template
  const limparAnexosNodesRemovidos = useCallback(
    (nodesAtuais = []) => {
      const entries = Object.entries(nodeAnexosById || {});
      entries.forEach(([, entry]) => {
        (entry?.newFiles || []).forEach((arquivo) => {
          if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
        });
      });
      const idsSalvos = entries.flatMap(([, entry]) =>
        entry?.loaded
          ? (entry?.saved || []).map((arquivo) => arquivo?.id).filter(Boolean)
          : [],
      );
      if (idsSalvos?.length) {
        setCascadeDeletedAnexoIds((prev) => [...prev, ...idsSalvos]);
      }
      (nodesAtuais || []).forEach((node) => {
        const nodeId = node?.id;
        if (!nodeId || nodeAnexosById?.[nodeId]?.loaded) return;
        api
          .post("/anexos/findMany", {
            entityType: ANEXO_ENTITY_TYPE_NODE,
            entityId: buildEntityId(nodeId),
          })
          .then((res) => {
            const ids = (res?.data || [])
              .map((arquivo) => arquivo?.id)
              .filter(Boolean);
            if (ids?.length) {
              setCascadeDeletedAnexoIds((prev) => [...prev, ...ids]);
            }
          })
          .catch((error) =>
            console.error(
              "Erro ao buscar anexos em segundo plano para limpeza de template:",
              error,
            ),
          );
      });
      setNodeAnexosById({});
      nodeAnexosFetchStatusRef.current = {};
    },
    [nodeAnexosById, buildEntityId],
  );

  // Executa o descarte de previews e enfileiramento de remoção de anexos dos nós indicados
  const removerAnexosDosNodes = useCallback(
    (nodeIds = []) => {
      (nodeIds || []).forEach((id) => {
        prepararRemocaoDeAnexosDoNode(id);
      });
      setNodeAnexosById((prev) => {
        const proximo = { ...prev };
        (nodeIds || []).forEach((id) => delete proximo[id]);
        return proximo;
      });
      (nodeIds || []).forEach((id) => delete nodeAnexosFetchStatusRef.current[id]);
    },
    [prepararRemocaoDeAnexosDoNode],
  );

  // Verifica se um nó possui anexos salvos ou com carregamento em andamento
  const nodeTemAnexosSalvos = useCallback(
    (nodeId) => {
      const entry = nodeAnexosById?.[nodeId];
      const fetchStatus = nodeAnexosFetchStatusRef?.current?.[nodeId];
      return (
        (entry?.loaded && (entry?.saved || [])?.length > 0) ||
        fetchStatus === "loading"
      );
    },
    [nodeAnexosById],
  );

  // Semeia status de fetch dos nós fornecidos como carregado para evitar refetch desnecessário
  const semearComoCarregado = useCallback((nodeIds = []) => {
    setNodeAnexosById((prev) => {
      const proximo = { ...prev };
      (nodeIds || []).forEach((id) => {
        proximo[id] = { ...emptyNodeAnexosEntry, loaded: true };
        nodeAnexosFetchStatusRef.current[id] = "done";
      });
      return proximo;
    });
  }, []);

  // Revoga URLs blob e descarta todos os anexos locais pendentes
  const descartarPendentes = useCallback(() => {
    setNodeAnexosById((prev) => {
      Object.values(prev || {}).forEach((entry) => {
        (entry?.newFiles || []).forEach((arquivo) => {
          if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
        });
      });
      return {};
    });
    setCascadeDeletedAnexoIds([]);
    nodeAnexosFetchStatusRef.current = {};
  }, []);

  // Agrega criações e exclusões pendentes para envio no upsert do circuito
  const getPendingNodeAnexos = useCallback(
    (circuitoIdOverride) => {
      const idParaEntityId = circuitoIdOverride || circuitoId;
      const buildId = (nodeId) =>
        idParaEntityId ? `${idParaEntityId}:${nodeId}` : nodeId;
      return {
        creates: Object.entries(nodeAnexosById || {}).flatMap(
          ([nodeId, entry]) =>
            (entry?.newFiles || []).map((file) => ({
              entityId: buildId(nodeId),
              originalFile: file?.originalFile,
              name: file?.name,
              type: file?.type,
            })),
        ),
        deletes: [
          ...Object.values(nodeAnexosById || {}).flatMap(
            (entry) => entry?.deletedIds || [],
          ),
          ...(cascadeDeletedAnexoIds || []),
        ],
      };
    },
    [circuitoId, nodeAnexosById, cascadeDeletedAnexoIds],
  );

  // Ressincroniza anexos salvos após retorno do upsert no backend
  const refreshNodeAnexosAfterSave = useCallback(
    async (circuitoIdOverride, falhas = {}) => {
      const idParaEntityId = circuitoIdOverride || circuitoId;
      const buildId = (nodeId) =>
        idParaEntityId ? `${idParaEntityId}:${nodeId}` : nodeId;
      const criacoesFalhadas = falhas?.creates || [];
      const delecoesFalhadas = new Set(falhas?.deletes || []);
      const nodeIdsAtuais = new Set(
        (nodesRef?.current || []).map((node) => node?.id),
      );
      const idsParaAtualizar = Object.keys(nodeAnexosById || {}).filter((id) =>
        nodeIdsAtuais.has(id),
      );
      const resultados = await Promise.all(
        idsParaAtualizar.map((nodeId) =>
          api
            .post("/anexos/findMany", {
              entityType: ANEXO_ENTITY_TYPE_NODE,
              entityId: buildId(nodeId),
            })
            .then((res) => [nodeId, res?.data || []])
            .catch(() => [nodeId, []]),
        ),
      );
      setNodeAnexosById((prev) => {
        const proximo = { ...prev };
        resultados.forEach(([nodeId, saved]) => {
          const arquivosQueFalharam = new Set(
            criacoesFalhadas
              .filter((criacao) => criacao?.entityId === buildId(nodeId))
              .map((criacao) => criacao?.originalFile),
          );
          (prev?.[nodeId]?.newFiles || [])
            .filter(
              (arquivo) => !arquivosQueFalharam.has(arquivo?.originalFile),
            )
            .forEach((arquivo) => {
              if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
            });
          proximo[nodeId] = {
            ...emptyNodeAnexosEntry,
            saved,
            loaded: true,
            newFiles: (prev?.[nodeId]?.newFiles || []).filter((arquivo) =>
              arquivosQueFalharam.has(arquivo?.originalFile),
            ),
            deletedIds: (prev?.[nodeId]?.deletedIds || []).filter((id) =>
              delecoesFalhadas.has(id),
            ),
          };
        });
        return proximo;
      });
      resultados.forEach(([nodeId]) => {
        nodeAnexosFetchStatusRef.current[nodeId] = "done";
      });
      setCascadeDeletedAnexoIds((prev) =>
        (prev || []).filter((id) => delecoesFalhadas.has(id)),
      );
    },
    [circuitoId, nodeAnexosById, nodesRef],
  );

  // Extrai snapshot de anexos para a pilha de histórico do diagrama
  const capturarAnexosSnapshot = useCallback(
    () => ({
      nodeAnexosByIdSnapshot: { ...nodeAnexosById },
      cascadeDeletedAnexoIdsSnapshot: [...cascadeDeletedAnexoIds],
    }),
    [nodeAnexosById, cascadeDeletedAnexoIds],
  );

  // Restaura anexos a partir de um snapshot do histórico
  const restaurarAnexosSnapshot = useCallback((snapshot) => {
    setCascadeDeletedAnexoIds(snapshot?.cascadeDeletedAnexoIdsSnapshot || []);
    setNodeAnexosById((atual) => {
      Object.values(atual || {}).forEach((entry) => {
        (entry?.newFiles || []).forEach((arquivo) => {
          if (arquivo?.preview) URL.revokeObjectURL(arquivo.preview);
        });
      });
      return Object.fromEntries(
        Object.entries(snapshot?.nodeAnexosByIdSnapshot || {}).map(
          ([nodeId, entry]) => [
            nodeId,
            {
              ...entry,
              newFiles: (entry?.newFiles || []).map((f) => ({
                ...f,
                preview:
                  f?.originalFile instanceof File
                    ? URL.createObjectURL(f.originalFile)
                    : f?.preview,
              })),
            },
          ],
        ),
      );
    });
  }, []);

  return {
    nodeAnexosById,
    setNodeAnexosById,
    cascadeDeletedAnexoIds,
    setCascadeDeletedAnexoIds,
    nodeAnexosFetchStatusRef,
    carregarAnexosDoNode,
    addNodeAnexoFiles,
    removeNodeAnexoFile,
    adicionarAnexoLocal: addNodeAnexoFiles,
    marcarAnexoParaExclusao: removeNodeAnexoFile,
    downloadNodeAnexoFile,
    prepararRemocaoDeAnexosDoNode,
    enfileirarCascadeDelete,
    limparAnexosNodesRemovidos,
    semearComoCarregado,
    descartarPendentes,
    getPendingNodeAnexos,
    refreshNodeAnexosAfterSave,
    capturarAnexosSnapshot,
    restaurarAnexosSnapshot,
    removerAnexosDosNodes,
    nodeTemAnexosSalvos,
  };
}
